import { useState } from "react";
import type { FunnelReport } from "@/api";
import { fmtMs } from "@/utils/format";
import { TimeSeriesChart } from "@/components/TimeSeriesChart";
import { DevicesCard } from "@/components/DevicesCard";
import { PagesTable, type Denominator } from "@/components/PagesTable";

function Stat({ label, value, hint }: { label: string; value: number | string; hint?: string }) {
  return (
    <div className="card p-5">
      <div className="label">{label}</div>
      <div className="mt-2 font-display text-4xl font-bold tabular-nums text-text">{value}</div>
      {hint && <div className="mt-1 text-xs text-faint">{hint}</div>}
    </div>
  );
}

export function FunnelTable({
  report,
  activePersona,
  onPersonaToggle,
}: {
  report: FunnelReport;
  /** Persona currently filtering the report (chips render as active). */
  activePersona?: string | null;
  /** When given, persona chips become filter toggles. */
  onPersonaToggle?: (persona: string | null) => void;
}) {
  const rate = report.completion_rate;
  const personas = Object.entries(report.persona_counts);
  const [denominator, setDenominator] = useState<Denominator>("answered");
  const hasEngagement = report.results_viewed != null || report.cta_clicks != null;

  return (
    <div className="rise space-y-8">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Visitors" value={report.started} hint="Sessions that opened the quiz" />
        <Stat label="Completions" value={report.completed} hint="Sessions that reached the result" />
        <div className="card p-5">
          <div className="label">Completion rate</div>
          <div className="mt-2 font-display text-4xl font-bold tabular-nums text-lime">
            {rate === null ? "—" : `${rate}%`}
          </div>
          {rate !== null && (
            <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-ink-3">
              <div
                className="h-full rounded-full bg-lime shadow-[0_0_10px_rgba(211,255,78,0.6)]"
                style={{ width: `${Math.min(100, Math.max(0, rate))}%` }}
              />
            </div>
          )}
        </div>
        <Stat
          label="Time to complete"
          value={fmtMs(report.avg_duration_ms)}
          hint="Average across completions"
        />
      </div>

      {hasEngagement && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Stat label="Leads" value={report.submissions} hint="Stored submissions" />
          <Stat label="Result views" value={report.results_viewed ?? 0} hint="Sessions that saw their result" />
          <Stat label="CTA clicks" value={report.cta_clicks ?? 0} hint="Sessions that clicked the offer" />
        </div>
      )}

      {(report.daily?.length || report.devices) && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          {report.daily && report.daily.length > 0 && (
            <div className="lg:col-span-2">
              <TimeSeriesChart daily={report.daily} />
            </div>
          )}
          {report.devices && <DevicesCard devices={report.devices} />}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="label">Pages</h2>
        <div role="group" aria-label="Breakdown denominator" className="flex gap-1 text-xs">
          {(["answered", "started"] as const).map((d) => (
            <button
              key={d}
              type="button"
              aria-pressed={denominator === d}
              className={`rounded-full px-3 py-1 font-semibold ${
                denominator === d ? "bg-ink-3 text-text" : "text-dim hover:text-text"
              }`}
              onClick={() => setDenominator(d)}
            >
              % of {d}
            </button>
          ))}
        </div>
      </div>
      <PagesTable report={report} denominator={denominator} />

      {personas.length > 0 && (
        <div>
          <h2 className="label mb-3">Personas</h2>
          <ul className="flex flex-wrap gap-2">
            {personas.map(([persona, count]) => {
              const active = activePersona === persona;
              const chip = (
                <>
                  <span>{persona}</span>
                  <span className="font-semibold text-text">{count}</span>
                </>
              );
              return (
                <li key={persona}>
                  {onPersonaToggle ? (
                    <button
                      type="button"
                      aria-pressed={active}
                      className={`tag gap-2 ${active ? "border-lime text-lime" : "hover:text-text"}`}
                      onClick={() => onPersonaToggle(active ? null : persona)}
                    >
                      {chip}
                    </button>
                  ) : (
                    <span className="tag gap-2">{chip}</span>
                  )}
                </li>
              );
            })}
          </ul>
          {onPersonaToggle && (
            <p className="mt-2 text-xs text-faint">Click a persona to filter the report to its sessions.</p>
          )}
        </div>
      )}
    </div>
  );
}
